// `RadioScript`, `RadioTypst`, `RadioDocx` (SuiteCompile/Radio.swift; `design-docs/Musicals, radio and sitcoms — design.md`,
// step 4): a radio play in the BBC's scene style — Arial 12 on A4 with lines half again apart, "SCENE 3." and every
// technical direction 1.5 in into the page in capitals and underlined, each name at the margin with its speech hanging an
// inch in, voice directions in capitals inside the speech, END. at the end, pages numbered at the foot; a studio script
// numbers the speeches from 1 on each page.
import { fountainParagraphs, fountainParagraphText } from '@writing-suite/core';
import { DOCX_SETTINGS, docxApp, docxCore, docxCustom, twips } from '../docx.ts';
import { pageInches } from '../manuscript.ts';
import type { CompiledManuscript, RenderSettings } from '../types.ts';
import { escape } from '../typst/writer.ts';
import { ZipWriter } from '../zip.ts';
import { speaker, type Paragraph, type Run } from './script.ts';
import { emptyStageScript, stageLayout, stageScript, type StageScript } from './stage.ts';
import { docxBits } from './stageDocx.ts';
import { stageUKMarkup, stageUKSetup, stageUKSource, type StageUKSetup } from './stageTypst.ts';

const blank = (s: string) => /^\p{White_Space}*$/u.test(s);

export interface RadioScene {
  /** Its number through the play, from 1. */
  number: number;
  /** A folder's title over its first scene ("PART ONE"). */
  partHeading: string | null;
  paragraphs: Paragraph[];
  fragmentID: string | null;
}

export interface RadioScript {
  /** The title page and the cast, as a play's are made, with no scenes of its own. */
  front: StageScript;
  scenes: RadioScene[];
}

/** `RadioScript.init`. */
export function radioScript(m: CompiledManuscript): RadioScript {
  const front: StageScript = { ...stageScript(m), description: 'A Radio Play', parts: [], setting: [] };
  const scenes: RadioScene[] = [];
  let heading: string | null = null;
  for (const section of m.sections) {
    if (section.matter !== null) continue;
    const written = section.source !== null && !blank(section.source) ? section.source : null;
    if (section.role === 'part') heading = section.title.toUpperCase();
    if (written === null || !['part', 'chapter', 'scene'].includes(section.role)) continue;
    scenes.push({ number: scenes.length + 1, partHeading: heading, paragraphs: fountainParagraphs(written), fragmentID: section.fragmentID });
    heading = null;
  }
  return { front, scenes };
}

/** Where Arial isn't installed (the web, some Linux), the free faces metric-compatible with it. */
export const RADIO_FALLBACKS = ['Arial', 'Liberation Sans', 'Arimo', 'Helvetica'];

const radioSetup = (m: CompiledManuscript): StageUKSetup => ({ ...stageUKSetup(m), fallbacks: RADIO_FALLBACKS });

/** `RadioTypst.source(_ m:)`. */
export function radioTypst(m: CompiledManuscript): string {
  return radioSource(radioScript(m), radioSetup(m), m.preset.render.screenplay?.speechNumbers === 'page');
}

/** `RadioTypst.source(_:setup:numbered:)`. */
export function radioSource(script: RadioScript, setup: StageUKSetup, numbered: boolean): string {
  let out = stageUKSource(script.front, setup);
  if (!script.scenes.length) return out;
  out += '#set par(leading: 1.05em, spacing: 1.6em)\n';
  out += '#let speechNumber = counter("speech")\n';
  out += '#set page(footer: context align(center)[#str(here().page() - locate(<play-start>).page() + 1)]';
  out += numbered ? ', header: speechNumber.update(0))\n' : ')\n';
  out += '#let direction(body) = block(spacing: 1.6em, pad(left: 1.5in, underline(upper(body))))\n';
  out += numbered
    ? '#let speech(name, body) = block(spacing: 1.6em, grid(columns: (1in, 1fr), [#place(dx: -0.45in)[#speechNumber.step()#context speechNumber.display()]#name], body))\n'
    : '#let speech(name, body) = block(spacing: 1.6em, grid(columns: (1in, 1fr), name, body))\n';
  out += '#metadata(none) <play-start>\n';
  script.scenes.forEach((scene, index) => {
    if (scene.partHeading !== null) out += `#align(center)[#underline[#strong[${escape(scene.partHeading)}]]]\n`;
    out += `#direction[SCENE ${scene.number}.]\n`;
    out += radioBody(scene.paragraphs);
    if (index === script.scenes.length - 1) out += '#direction[END.]\n';
  });
  return out;
}

/** `RadioTypst.body`. */
function radioBody(paragraphs: readonly Paragraph[]): string {
  let out = '';
  const lines = (p: Paragraph) => p.lines.map(stageUKMarkup).join(' \\\n');
  let i = 0;
  while (i < paragraphs.length) {
    const p = paragraphs[i];
    switch (p.element) {
      case 'character': {
        let j = i + 1;
        const parts: string[] = [];
        while (j < paragraphs.length && ['dialogue', 'parenthetical', 'lyric'].includes(paragraphs[j].element)) {
          const q = paragraphs[j];
          parts.push(q.element === 'parenthetical' ? `#upper[${lines(q)}]` : q.element === 'lyric' ? `#emph[${lines(q)}]` : lines(q));
          j += 1;
        }
        out += `#speech([${escape(speaker(fountainParagraphText(p)).toUpperCase())}:], [${parts.join(' ')}])\n`;
        i = j;
        continue;
      }
      case 'dialogue': case 'lyric': out += `#speech([], [${lines(p)}])\n`; break;
      case 'parenthetical': out += `#speech([], [#upper[${lines(p)}]])\n`; break;
      case 'action': case 'transition': case 'sceneHeading': out += `#direction[${lines(p)}]\n`; break;
      case 'centered': out += `#align(center)[${lines(p)}]\n`; break;
      case 'pageBreak': out += '#pagebreak(weak: true)\n'; break;
      default: out += `\n${lines(p)}\n`;
    }
    i += 1;
  }
  return out;
}

/** `RadioTypst.estimatedPages`: without Typst, a play's US pages with the scenes run on. */
export function radioEstimatedPages(m: CompiledManuscript): number {
  const paragraphs = radioScript(m).scenes.flatMap((s) => s.paragraphs);
  if (!paragraphs.length) return 0;
  return stageLayout({ ...emptyStageScript(), parts: [{ act: null, scene: null, headings: [], paragraphs, end: null }] }).pages.length;
}

/** `RadioTypst.pageCount(fountain:engine:)`: the pages a radio play's scenes print on (Write's footer), counted by Typst. */
export async function radioPageCount(texts: readonly string[], pages: (source: string) => Promise<number | null>): Promise<number | null> {
  const scenes = texts.filter((t) => !blank(t)).map((t, i): RadioScene => ({ number: i + 1, partHeading: null, paragraphs: fountainParagraphs(t), fragmentID: null }));
  if (!scenes.length) return 0;
  const setup: StageUKSetup = { width: 595.28, height: 841.89, margin: 72, font: 'Arial', fallbacks: RADIO_FALLBACKS, size: 12, title: '', language: 'en',
    author: null, titlePage: false, byline: true, contact: true };
  return pages(radioSource({ front: emptyStageScript(), scenes }, setup, false));
}

// MARK: Word

/** `RadioDocx.write`'s parts, in the order the Swift adds them. */
export function radioDocxEntries(m: CompiledManuscript, o: { date?: Date; properties?: Record<string, string> } = {}): [string, string][] {
  const D = docxBits;
  const script = radioScript(m);
  const r = m.preset.render;
  const { width, height } = pageInches(r.page.size);
  const numbered = r.screenplay?.speechNumbers === 'page';
  const properties = o.properties ?? {};
  const hasProperties = Object.keys(properties).length > 0;
  const sectPr = (footer: boolean, restart: boolean) => {
    const margins = `<w:pgMar w:top="${twips(r.page.margins)}" w:right="${twips(r.page.margins)}" w:bottom="${twips(r.page.margins)}" w:left="${twips(r.page.margins)}" w:header="${twips(0.5)}" w:footer="${twips(0.5)}" w:gutter="0"/>`;
    return '<w:sectPr>' + (footer ? '<w:footerReference w:type="default" r:id="rIdFooter1"/>' : '')
      + `<w:type w:val="nextPage"/><w:pgSz w:w="${twips(width)}" w:h="${twips(height)}"/>${margins}`
      + (numbered && footer ? '<w:lnNumType w:countBy="1" w:restart="newPage"/>' : '')
      + (restart ? '<w:pgNumType w:start="1"/>' : '') + '</w:sectPr>';
  };
  let body = D.frontMatter(script.front, m, true);
  if (body !== '' && script.scenes.length) body += `<w:p><w:pPr>${sectPr(false, false)}</w:pPr></w:p>`;
  script.scenes.forEach((scene, index) => {
    if (scene.partHeading !== null) body += D.paragraph('PartHeading', D.text(scene.partHeading), true);
    body += D.paragraph('SceneHeading', D.text(`SCENE ${scene.number}.`), true);
    body += paragraphsXML(scene.paragraphs);
    if (index === script.scenes.length - 1) body += D.paragraph('TechnicalDirection', D.text('END.'), true);
  });
  const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><w:body>${body}${sectPr(true, true)}</w:body></w:document>`;
  const parts: [string, string][] = [
    ['[Content_Types].xml', D.contentTypes(0, hasProperties).replace('</Types>', '<Override PartName="/word/footer1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/></Types>')],
    ['_rels/.rels', D.rootRels(hasProperties)],
    ['word/document.xml', document],
    ['word/styles.xml', styles(r, m.language, numbered)],
    ['word/settings.xml', DOCX_SETTINGS],
    ['word/_rels/document.xml.rels', D.documentRels(0).replace('</Relationships>', '<Relationship Id="rIdFooter1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer" Target="footer1.xml"/></Relationships>')],
    ['word/footer1.xml', FOOTER],
    ['docProps/core.xml', docxCore(m, o.date ?? new Date())],
    ['docProps/app.xml', docxApp(m)],
  ];
  if (hasProperties) parts.push(['docProps/custom.xml', docxCustom(properties)]);
  return parts;
}

export function writeRadioDocx(m: CompiledManuscript, o: { date?: Date; properties?: Record<string, string> } = {}): Uint8Array {
  const zip = new ZipWriter();
  for (const [name, text] of radioDocxEntries(m, o)) zip.add(name, text);
  return zip.finish();
}

function paragraphsXML(ps: readonly Paragraph[]): string {
  const D = docxBits;
  const upper = (runs: Run[]) => runs.map((r) => ({ ...r, text: r.text.toUpperCase() }));
  let out = '';
  let i = 0;
  while (i < ps.length) {
    const p = ps[i];
    switch (p.element) {
      case 'character': {
        let j = i + 1;
        const speech: string[] = [];
        while (j < ps.length && ['dialogue', 'parenthetical', 'lyric'].includes(ps[j].element)) {
          const q = ps[j];
          speech.push(q.lines.map((l) => D.runs(q.element === 'parenthetical' ? upper(l) : l, q.element === 'lyric')).join(D.LINE_BREAK));
          j += 1;
        }
        const name = D.text(speaker(fountainParagraphText(p)).toUpperCase() + ':');
        out += D.paragraph('Speech', name + D.TAB + speech.join(D.text(' ')), true);
        i = j;
        continue;
      }
      case 'dialogue': case 'parenthetical': case 'lyric':
        out += D.paragraph('Speech', D.TAB + p.lines.map((l) => D.runs(l, p.element === 'lyric')).join(D.LINE_BREAK), true);
        break;
      case 'pageBreak': out += D.PAGE_BREAK; break;
      case 'centered': out += D.paragraph('Centred', p.lines.map((l) => D.runs(l)).join(D.LINE_BREAK), true); break;
      default: out += D.paragraph('TechnicalDirection', p.lines.map((l) => D.runs(l)).join(D.LINE_BREAK), true);
    }
    i += 1;
  }
  return out;
}

/** The UK play's styles (for the pages before the play) and the radio play's own. */
function styles(r: RenderSettings, language: string, numbered: boolean): string {
  const base = docxBits.styles(r, true, language, pageInches(r.page.size).width - 2 * r.page.margins);
  const suppress = numbered ? '<w:suppressLineNumbers/>' : '';
  const style = (id: string, name: string, pPr = '', rPr = '') =>
    `<w:style w:type="paragraph" w:styleId="${id}"><w:name w:val="${name}"/><w:basedOn w:val="Normal"/><w:qFormat/>`
    + `<w:pPr>${pPr}</w:pPr>` + (rPr === '' ? '' : `<w:rPr>${rPr}</w:rPr>`) + '</w:style>';
  const oneAndAHalf = '<w:spacing w:line="360" w:lineRule="auto"/>';
  const directions = `<w:ind w:left="${twips(1.5)}"/>` + oneAndAHalf;
  const radio = [
    style('PartHeading', 'Part Heading', '<w:jc w:val="center"/><w:keepNext/>' + suppress, '<w:b/><w:caps/><w:u w:val="single"/>'),
    style('SceneHeading', 'Scene Heading', directions + '<w:keepNext/>' + suppress, '<w:caps/><w:u w:val="single"/>'),
    style('TechnicalDirection', 'Technical Direction', directions + suppress, '<w:caps/><w:u w:val="single"/>'),
    style('Speech', 'Speech', (numbered ? '<w:suppressLineNumbers w:val="0"/>' : '')
      + `<w:tabs><w:tab w:val="left" w:pos="${twips(1)}"/></w:tabs><w:ind w:left="${twips(1)}" w:hanging="${twips(1)}"/>` + oneAndAHalf),
  ].join('');
  // A studio script numbers only the speeches: everything else suppresses Word's line numbers.
  const front = numbered ? base.replaceAll('<w:pPr>', `<w:pPr>${suppress}`) : base;
  return front.replace('</w:styles>', radio + '</w:styles>');
}

const FOOTER = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
  + '<w:ftr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
  + '<w:p><w:pPr><w:jc w:val="center"/></w:pPr><w:r><w:fldChar w:fldCharType="begin"/></w:r><w:r><w:instrText xml:space="preserve"> PAGE </w:instrText></w:r><w:r><w:fldChar w:fldCharType="separate"/></w:r><w:r><w:t>1</w:t></w:r><w:r><w:fldChar w:fldCharType="end"/></w:r></w:p></w:ftr>';
